/**
 * A GLOBAL "IS ANYTHING LOADING" SIGNAL, decoupled from any one screen.
 *
 * The top progress bar (components/TopProgressBar) must light up for EVERY request the
 * dashboard makes — a report fetch, a store switch, an export poll — without each caller
 * remembering to toggle it. So the axios client (lib/api) reports every request's start
 * and end here, and this module tracks how many are in flight. The bar subscribes to the
 * derived "active" flag; it never counts requests itself.
 *
 * WHY A COUNTER, NOT A BOOLEAN. A dashboard fires several report calls at once. A boolean
 * set false by the first response to arrive would hide the bar while the others were
 * still loading; a count only reads idle when the LAST request settles.
 *
 * This is deliberately a module singleton, not React state or context: the axios
 * interceptors live OUTSIDE the component tree (createApiClient runs in a useMemo, not a
 * render) and must reach this without a provider.
 */

type Listener = (active: boolean) => void;

let inFlight = 0;
const listeners = new Set<Listener>();

function emit(): void {
  const active = inFlight > 0;
  for (const listener of listeners) listener(active);
}

/** Called by the axios request interceptor the moment a request is sent. */
export function notifyRequestStart(): void {
  inFlight += 1;
  // Only the 0 → 1 edge flips the app from idle to loading; further concurrent starts
  // don't change what the bar shows, so they don't need to wake every subscriber.
  if (inFlight === 1) emit();
}

/** Called once when a request settles — success OR failure, exactly once per start. */
export function notifyRequestEnd(): void {
  // A settled request never takes the count below zero. Without this guard an unbalanced
  // end (a start we somehow missed) would drive it negative, and the bar would then need
  // two real requests before it reappeared.
  if (inFlight === 0) return;
  inFlight -= 1;
  if (inFlight === 0) emit();
}

/**
 * Subscribe to the active flag. Returns an unsubscribe.
 *
 * Fires once immediately with the current state, so a bar that mounts while a request is
 * already in flight shows straight away instead of waiting for the next edge.
 */
export function subscribeToLoading(listener: Listener): () => void {
  listeners.add(listener);
  listener(inFlight > 0);
  return () => {
    listeners.delete(listener);
  };
}
